import { Fragment } from 'react';
import { type AnyChainflipAsset } from '@chainflip/utils/chainflip';
import { isNotNullish } from '@chainflip/utils/guard';
import classNames from 'classnames';
import { TokenWithChainByAsset } from '@/shared/components/logo/TokenWithChain';
import { Tooltip } from '@/shared/components/molecules/Tooltip';
import { type SwapRequestOverviewFragment } from '@/shared/graphql/generated/reporting/graphql';
import ArrowIcon from '@/shared/icons/flip-ui-kit/large/Arrow';
import RefundIcon from '@/shared/icons/flip-ui-kit/large/Refund';
import { anyChainflipAssetMap } from '@/shared/utils/env';
import { formatUsdValue, pipsToPercentString } from '@/shared/utils/numbers';
import { getOraclePriceDeltaColor } from '@/shared/utils/swaps';
import TokenAmount from '@/shared/utils/TokenAmount';

type SwapStep = { asset: AnyChainflipAsset; amount?: string; amountUsd?: string };

const getSwapSteps = (
  swapRequest: SwapRequestOverviewFragment,
  sourceAsset: AnyChainflipAsset,
  destinationAsset: AnyChainflipAsset,
  showIntermediate = true,
): [SwapStep, SwapStep] | [SwapStep, SwapStep, SwapStep] => {
  const sourceAmount = TokenAmount.fromAsset(swapRequest.totalInputAmount, sourceAsset);
  const sourceAmountValueUsd = swapRequest.totalInputValueUsd;
  const hasSourceAmount = (sourceAmount && !sourceAmount.eq(0)) || undefined;
  const sourceAmounts = {
    asset: sourceAsset,
    amount: hasSourceAmount && sourceAmount?.toFormatted(),
    amountUsd: hasSourceAmount && (sourceAmountValueUsd ?? undefined),
  };

  const swapOutputAmount = TokenAmount.fromAsset(swapRequest.outputAmount, destinationAsset);
  const hasSwapOutputAmount = (swapOutputAmount && !swapOutputAmount.eq(0)) || undefined;
  const swapOutputAmounts = {
    asset: destinationAsset,
    amount: hasSwapOutputAmount && swapOutputAmount?.toFormatted(),
    amountUsd: hasSwapOutputAmount && (swapRequest.outputValueUsd ?? undefined),
  };

  const egressAmount = TokenAmount.fromAsset(swapRequest.egressAmount, destinationAsset);
  const hasEgressAmount = (egressAmount && !egressAmount.eq(0)) || undefined;
  const egressAmounts = {
    asset: destinationAsset,
    amount: hasEgressAmount && egressAmount?.toFormatted(),
    amountUsd: hasEgressAmount && (swapRequest.egressValueUsd ?? undefined),
  };

  const intermediateAmount = TokenAmount.fromAsset(swapRequest.intermediateAmount, 'Usdc');
  const hasIntermediateAmount = (intermediateAmount && !intermediateAmount.eq(0)) || undefined;
  const intermediateAmounts = {
    asset: 'Usdc' as AnyChainflipAsset,
    amount: hasIntermediateAmount && intermediateAmount?.toFormatted('numeral', 2),
    amountUsd: hasIntermediateAmount && (swapRequest.intermediateValueUsd ?? undefined),
  };

  const destinationAmounts = hasEgressAmount ? egressAmounts : swapOutputAmounts;
  if (sourceAsset === 'Usdc' || destinationAsset === 'Usdc') {
    return [sourceAmounts, destinationAmounts];
  }

  if (showIntermediate) {
    return [sourceAmounts, intermediateAmounts, destinationAmounts];
  }
  return [sourceAmounts, destinationAmounts];
};

export const StepTokenAmount = ({
  asset,
  amount,
  usdAmount,
  oraclePriceDeltaBps,
  displayChainLogo = true,
  disabled = false,
}: {
  asset: AnyChainflipAsset;
  amount?: string;
  usdAmount?: string | number;
  oraclePriceDeltaBps?: string | null;
  displayChainLogo?: boolean;
  disabled?: boolean;
}) => {
  const oraclePriceDeltaBpsToNumber = isNotNullish(oraclePriceDeltaBps)
    ? Number(oraclePriceDeltaBps)
    : null;

  return (
    <div className={classNames(disabled && 'opacity-30', 'flex w-[138px] flex-col gap-y-0.5')}>
      <div className="flex items-center gap-x-1">
        <TokenWithChainByAsset asset={asset} size="small" displayChainLogo={displayChainLogo} />
        {amount && (
          <span className="text-12 text-white">
            {amount} {anyChainflipAssetMap[asset].symbol}
          </span>
        )}
      </div>

      <div className="flex items-center gap-x-1">
        {usdAmount !== undefined && (
          <span className="text-[11px] text-cf-light-2 lg:text-12">
            {formatUsdValue(usdAmount)}
          </span>
        )}
        {isNotNullish(oraclePriceDeltaBpsToNumber) && (
          <Tooltip content="The difference between the oracle price and the execution rate excluding fees and Broker commission">
            <span className={getOraclePriceDeltaColor(oraclePriceDeltaBpsToNumber)}>
              ({pipsToPercentString(oraclePriceDeltaBpsToNumber)})
            </span>
          </Tooltip>
        )}
      </div>
    </div>
  );
};

const Route = ({
  steps,
  status,
  swapRequest,
  highlightAssets,
  showOracleDelta = false,
  hideUsdValue = false,
}: {
  steps: [SwapStep, SwapStep] | [SwapStep, SwapStep, SwapStep];
  status: SwapRequestOverviewFragment['status'];
  swapRequest?: SwapRequestOverviewFragment & { sourceAsset: AnyChainflipAsset };
  highlightAssets?: AnyChainflipAsset[];
  showOracleDelta?: boolean;
  hideUsdValue?: boolean;
}) =>
  status === 'REFUND_SUCCESS' && swapRequest ? (
    <div className="flex items-center gap-x-4 px-0.5 py-px">
      <StepTokenAmount
        asset={swapRequest.sourceAsset}
        amount={TokenAmount.fromAsset(
          swapRequest.totalInputAmount,
          swapRequest.sourceAsset,
        )?.toFormatted()}
        usdAmount={hideUsdValue ? undefined : (swapRequest.totalInputValueUsd ?? undefined)}
        displayChainLogo
        disabled={highlightAssets && !highlightAssets.includes(swapRequest.sourceAsset)}
      />
      <RefundIcon width={16} className="block text-cf-light-2" />
      <StepTokenAmount
        asset={swapRequest.sourceAsset}
        amount={TokenAmount.fromAsset(
          swapRequest.refundAmount,
          swapRequest.sourceAsset,
        )?.toFormatted()}
        usdAmount={hideUsdValue ? undefined : (swapRequest.refundValueUsd ?? undefined)}
        displayChainLogo
        disabled={highlightAssets && !highlightAssets.includes(swapRequest.sourceAsset)}
      />
    </div>
  ) : (
    <div className="flex items-center gap-x-4 px-0.5 py-px">
      {steps.map((step, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <Fragment key={`${step.asset}-${index}`}>
          <StepTokenAmount
            asset={step.asset}
            amount={step.amount}
            usdAmount={hideUsdValue ? undefined : step.amountUsd}
            oraclePriceDeltaBps={
              showOracleDelta && index + 1 === steps.length
                ? (swapRequest?.oraclePriceDeltaExFeesBps ?? swapRequest?.oraclePriceDeltaBps)
                : undefined
            }
            displayChainLogo={!(index === 1 && steps.length > 2)}
            disabled={highlightAssets && !highlightAssets.includes(step.asset)}
          />
          <ArrowIcon
            width={16}
            className={classNames(
              'text-cf-light-2',
              index + 1 === steps.length ? 'hidden' : 'block',
            )}
          />
        </Fragment>
      ))}
    </div>
  );

export const CompactSwapRoute = ({
  routeInfo: { sourceAsset, destinationAsset },
  swapRequest,
  status,
  highlightAssets,
  showIntermediate = true,
  showOracleDelta = false,
  hideUsdValue = false,
}: {
  routeInfo: {
    sourceAsset: AnyChainflipAsset;
    destinationAsset: AnyChainflipAsset;
  };
  swapRequest: SwapRequestOverviewFragment;
  status: SwapRequestOverviewFragment['status'];
  highlightAssets?: AnyChainflipAsset[];
  showIntermediate?: boolean;
  showOracleDelta?: boolean;
  hideUsdValue?: boolean;
}) => {
  if (!sourceAsset || !destinationAsset) return null;
  const steps = getSwapSteps(swapRequest, sourceAsset, destinationAsset, showIntermediate);

  return (
    <Route
      steps={steps}
      status={status}
      swapRequest={swapRequest as SwapRequestOverviewFragment & { sourceAsset: AnyChainflipAsset }}
      highlightAssets={highlightAssets}
      showOracleDelta={showOracleDelta}
      hideUsdValue={hideUsdValue}
    />
  );
};
